import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, downloadFile } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { formatMoney } from "@/lib/format";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, Loader2, Scale } from "lucide-react";

const TABS = [
  { id: "journal", label: "Journal" },
  { id: "tb", label: "Trial Balance" },
];

export default function Journal() {
  const { currency } = useCurrency();
  const [tab, setTab] = useState("journal");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [journal, setJournal] = useState(null);
  const [tb, setTb] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = `currency=${currency}&from_date=${from}&to_date=${to}`;
      const [j, t] = await Promise.all([
        api.get(`/gl/journal?${qs}`),
        api.get(`/gl/trial-balance?${qs}`),
      ]);
      setJournal(j.data);
      setTb(t.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the general ledger");
    } finally {
      setLoading(false);
    }
  }, [currency, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const exportGl = async () => {
    try {
      await downloadFile(
        `/gl/journal/export?format=xlsx&currency=${currency}&from_date=${from}&to_date=${to}`,
        "journal_trial_balance.xlsx"
      );
    } catch {
      toast.error("Export failed");
    }
  };

  const balanced = tb && Math.abs(tb.total_debit - tb.total_credit) < 1;

  return (
    <div className="space-y-5" data-testid="journal-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">General Ledger</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Every book event as a balanced double-entry voucher — accruals, settlements, transfers,
            sweeps, FDs and assets. Projected live from the sub-ledgers, so it can never drift.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="journal-from"
            className="h-10 rounded-md bg-background border border-input px-2 text-xs"
          />
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="journal-to"
            className="h-10 rounded-md bg-background border border-input px-2 text-xs"
          />
          <Button variant="outline" size="sm" onClick={exportGl} className="gap-2 h-10" data-testid="journal-export">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {tb && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            ["Vouchers", journal?.count ?? 0, "journal-kpi-count"],
            ["Total debit", formatMoney(tb.total_debit, currency, { compact: true }), "journal-kpi-debit"],
            ["Total credit", formatMoney(tb.total_credit, currency, { compact: true }), "journal-kpi-credit"],
            [
              "Books balanced",
              balanced ? "Yes · Dr = Cr" : `Off by ${formatMoney(Math.abs(tb.total_debit - tb.total_credit), currency)}`,
              "journal-kpi-balanced",
            ],
          ].map(([label, value, tid]) => (
            <Card key={tid} className="p-4" data-testid={tid}>
              <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
              <p className={`font-heading text-lg mt-1.5 font-mono ${tid === "journal-kpi-balanced" && !balanced ? "text-rose-300" : ""}`}>
                {value}
              </p>
            </Card>
          ))}
        </div>
      )}

      <div className="flex gap-1.5">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`journal-tab-${t.id}`}
            className={`px-3 py-1.5 rounded-md text-xs transition-colors ${
              tab === t.id
                ? "bg-brand/15 text-foreground border border-brand/30"
                : "text-muted-foreground border border-white/5 hover:bg-white/[0.04]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-24 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : tab === "journal" ? (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="journal-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
                <th className="text-left px-4 py-3">Voucher</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Narration</th>
                <th className="text-left px-4 py-3">Account</th>
                <th className="text-right px-4 py-3">Debit</th>
                <th className="text-right px-4 py-3">Credit</th>
              </tr>
            </thead>
            <tbody>
              {(journal?.entries || [])
                .slice()
                .reverse()
                .map((e) =>
                  e.lines.map((l, idx) => (
                    <tr
                      key={`${e.id}-${idx}`}
                      className={`hover:bg-white/[0.02] ${
                        idx === e.lines.length - 1 ? "border-b border-white/[0.05]" : ""
                      }`}
                      data-testid={idx === 0 ? `journal-row-${e.voucher_no}` : undefined}
                    >
                      <td className="px-4 py-2 font-mono text-[11px] text-brand">
                        {idx === 0 ? e.voucher_no : ""}
                      </td>
                      <td className="px-4 py-2 font-mono text-[11px]">{idx === 0 ? e.date : ""}</td>
                      <td className="px-4 py-2 text-xs">{idx === 0 ? e.type : ""}</td>
                      <td className="px-4 py-2 text-xs max-w-[280px] truncate" title={e.narration}>
                        {idx === 0 ? e.narration : ""}
                      </td>
                      <td className="px-4 py-2 text-xs">{l.account}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {l.debit ? formatMoney(l.debit, currency, { compact: true }) : ""}
                      </td>
                      <td className="px-4 py-2 text-right font-mono text-xs">
                        {l.credit ? formatMoney(l.credit, currency, { compact: true }) : ""}
                      </td>
                    </tr>
                  ))
                )}
            </tbody>
          </table>
          {!journal?.entries?.length && (
            <p className="p-10 text-center text-sm text-muted-foreground" data-testid="journal-empty">
              No vouchers yet. Upload an invoice or a bank statement to start the books.
            </p>
          )}
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="trial-balance-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b border-white/5">
                <th className="text-left px-4 py-3">Account</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">Debit</th>
                <th className="text-right px-4 py-3">Credit</th>
                <th className="text-right px-4 py-3">Balance</th>
              </tr>
            </thead>
            <tbody>
              {(tb?.rows || []).map((r) => (
                <tr
                  key={r.account}
                  className="border-b border-white/[0.03] hover:bg-white/[0.02]"
                  data-testid={`tb-row-${r.account}`}
                >
                  <td className="px-4 py-2.5 text-xs">{r.account}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground">{r.type}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs">
                    {r.debit ? formatMoney(r.debit, currency, { compact: true }) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs">
                    {r.credit ? formatMoney(r.credit, currency, { compact: true }) : "—"}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono text-xs">
                    {formatMoney(r.balance, currency, { compact: true })} {r.balance_side}
                  </td>
                </tr>
              ))}
              {tb && (
                <tr className="bg-white/[0.03] font-medium">
                  <td className="px-4 py-3 text-xs" colSpan={2}>
                    <span className="inline-flex items-center gap-2">
                      <Scale className="h-3.5 w-3.5 text-brand" /> Total
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {formatMoney(tb.total_debit, currency, { compact: true })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {formatMoney(tb.total_credit, currency, { compact: true })}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {balanced ? "Balanced" : "Check"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
